import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import RestartAltIcon from '@mui/icons-material/RestartAltRounded';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';

import { questionnaireApi } from '../../api/questionnaireApi';
import { reportsApi } from '../../api/reportsApi';
import FormationResponseCard from '../../components/dashboard/questionnaire/responses/FormationResponseCard';
import SessionResponseCard from '../../components/dashboard/questionnaire/responses/SessionResponseCard';
import ResponsesTabs from '../../components/dashboard/questionnaire/responses/ResponsesTabs';
import {
  APPRENANT_PATH,
  COMPTE_RENDU_TAB,
  buildFormationCards,
  groupKey,
  sortEntries
} from '../../components/dashboard/questionnaire/responses/constants';
import ScrollJumpButtons, { findScrollParent } from '../../components/ScrollJumpButtons';
import ArrowLeftIcon from '../../icons/ArrowLeft';
import { alpha } from '@mui/material/styles';
import { grey, lightVeil, greyAlpha, toneAlpha } from '../../theme/tones';

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Session la plus récente' },
  { value: 'date_asc', label: 'Session la plus ancienne' },
  { value: 'responses_desc', label: 'Le plus de réponses' },
  { value: 'formation_asc', label: 'Formation (A → Z)' }
];

const accent = COMPTE_RENDU_TAB.accent;

/**
 * Comptes rendus de session.
 *
 * Le document Word est monté au niveau de la SESSION et couvre les trois types
 * de questionnaires à la fois : il n'appartient donc à aucun onglet. Cette page
 * lui donne sa place propre, sur la même mécanique de navigation à deux niveaux
 * que « Résultats des questionnaires » — formations puis sessions — mais avec
 * le téléchargement comme seule action.
 *
 * Elle ne liste que les sessions ayant au moins une réponse : un compte rendu
 * sans réponse n'a rien à dire. D'où `type=All`, qui prend l'union des trois
 * types plutôt que celui de l'onglet d'où l'on vient.
 *
 * Arrivée avec `?formation=…&session=…` (depuis la barre d'onglets), la page
 * s'ouvre directement sur les sessions de cette formation, celle qui est visée
 * en tête de liste et cerclée. Sans paramètre, on repart de la liste complète.
 */
const ComptesRendus = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const contextType = searchParams.get('type');
  const contextSessionId = searchParams.get('session');

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  // Le niveau courant vit dans l'URL : c'est ce qui permet d'arriver
  // directement sur une formation, et de la retrouver au rafraîchissement.
  const openFormationId = searchParams.get('formation');

  const requestId = useRef(0);
  const anchorRef = useRef(null);
  const getPageScroller = useCallback(() => findScrollParent(anchorRef.current), []);

  /* --- chargement ------------------------------------------------- */

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    const current = ++requestId.current;

    (async () => {
      try {
        setLoading(true);
        const accessToken = window.localStorage.getItem('accessToken');
        const res = await questionnaireApi.fetchSubmissionsOverview(
          'All',
          { search: debouncedSearch },
          accessToken,
          false,
          controller.signal
        );
        if (current !== requestId.current) return;

        if (res && res.status) {
          setGroups(res.groups || []);
        } else {
          setGroups([]);
          if (res?.message) toast.error(res.message);
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error(err);
      } finally {
        if (current === requestId.current) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [debouncedSearch]);

  /* --- navigation --------------------------------------------------- */

  const patchParams = useCallback((patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openFormation = useCallback((formationId) => {
    // On quitte la session ciblée en changeant de formation : la garder
    // surlignerait une session qui n'est plus à l'écran.
    patchParams({ formation: formationId, session: null });
  }, [patchParams]);

  const backToFormations = useCallback(() => {
    patchParams({ formation: null, session: null });
  }, [patchParams]);

  /* --- données ------------------------------------------------------ */

  const sortedGroups = useMemo(() => sortEntries(groups, sortBy), [groups, sortBy]);

  const formationCards = useMemo(
    () => sortEntries(buildFormationCards(groups), sortBy),
    [groups, sortBy]
  );

  const currentFormation = useMemo(
    () => (openFormationId
      ? formationCards.find((f) => String(f.formation_id) === String(openFormationId)) || null
      : null),
    [formationCards, openFormationId]
  );

  // La session visée passe en tête : sur une formation à vingt sessions, la
  // surligner sans la remonter ne servirait à rien.
  const formationSessions = useMemo(() => {
    if (!openFormationId) return [];
    const list = sortedGroups.filter((g) => String(g.formation_id) === String(openFormationId));
    if (!contextSessionId) return list;
    const targeted = (g) => String(g.session_id) === String(contextSessionId);
    return [...list.filter(targeted), ...list.filter((g) => !targeted(g))];
  }, [sortedGroups, openFormationId, contextSessionId]);

  // Une formation qui disparaît (recherche trop restrictive) ne doit pas
  // laisser un écran vide : on remonte au niveau des formations.
  useEffect(() => {
    if (!openFormationId || loading) return;
    if (!groups.some((g) => String(g.formation_id) === String(openFormationId))) {
      backToFormations();
    }
  }, [groups, openFormationId, loading, backToFormations]);

  /* --- téléchargement ----------------------------------------------- */

  const handleDownload = async (group) => {
    const toastId = 'compte-rendu';
    try {
      setDownloadingId(group.session_id);
      toast.loading('Préparation du compte rendu…', { id: toastId });

      const accessToken = window.localStorage.getItem('accessToken');
      const blob = await reportsApi.downloadSessionWordReport(group.session_id, accessToken);

      saveAs(blob, `Compte_rendu_session_${group.session_number ?? group.session_id}.docx`);
      toast.success('Compte rendu téléchargé', { id: toastId });
    } catch (error) {
      toast.error(error.message || 'Compte rendu indisponible', { id: toastId });
    } finally {
      setDownloadingId(null);
    }
  };

  /* --- rendu -------------------------------------------------------- */

  const backTo = `${APPRENANT_PATH}?type=${encodeURIComponent(contextType || 'Satisfaction')}${
    openFormationId ? `&formation=${encodeURIComponent(openFormationId)}` : ''
  }`;

  const countLabel = openFormationId
    ? `${formationSessions.length} session${formationSessions.length > 1 ? 's' : ''}`
    : `${formationCards.length} formation${formationCards.length > 1 ? 's' : ''}`;

  const downloadCta = {
    label: 'Compte rendu',
    icon: <DownloadIcon sx={{ fontSize: 15 }} />,
    alwaysVisible: true
  };

  return (
    <>
      <Helmet>
        <title>Comptes rendus de session | Qualité | Apteed</title>
      </Helmet>

      <Box ref={anchorRef} sx={{ bgcolor: 'action.hover', minHeight: '100%', py: 3 }}>
        <Container maxWidth={false} disableGutters sx={{ px: 2 }}>

          {/* ── En-tête ───────────────────────────────────────────── */}
          <Box
            sx={{
              mb: 2.5,
              p: 3,
              borderRadius: '24px',
              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
              background: `linear-gradient(135deg, ${lightVeil(0.96)} 0%, ${greyAlpha(100, 0.75)} 100%)`,
              boxShadow: (theme) => `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Box
                sx={{
                  width: 54, height: 54, borderRadius: '16px', flexShrink: 0,
                  background: COMPTE_RENDU_TAB.gradient,
                  display: 'grid', placeItems: 'center',
                  boxShadow: `0 8px 20px ${accent}4D`
                }}
              >
                <DescriptionIcon sx={{ color: grey(0), fontSize: '1.6rem' }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.45rem', color: 'text.primary', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  Comptes rendus de session
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: '0.87rem', mt: 0.5 }}>
                  {COMPTE_RENDU_TAB.hint}
                </Typography>
              </Box>
            </Box>

            <Button
              component={RouterLink}
              to={backTo}
              startIcon={<ArrowLeftIcon style={{ fontSize: 13 }} />}
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}
            >
              Retour aux réponses
            </Button>
          </Box>

          {/* ── Sélecteur de type ─────────────────────────────────── */}
          <ResponsesTabs
            active={COMPTE_RENDU_TAB.value}
            context={{ type: contextType, formationId: openFormationId, sessionId: contextSessionId }}
          />

          {/* ── Filtres ───────────────────────────────────────────── */}
          <Card sx={{ p: 2, mb: 2.5, borderRadius: '18px', border: `1px solid ${grey(100)}`, boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}` }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Formation, apprenant, e-mail…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                  flex: '1 1 260px',
                  minWidth: 220
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  )
                }}
              />

              <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'text.primary' }}>
                {loading ? 'Chargement…' : countLabel}
              </Typography>

              {search !== '' && (
                <Chip
                  label="Recherche active"
                  size="small"
                  onDelete={() => setSearch('')}
                  deleteIcon={<RestartAltIcon sx={{ fontSize: 15 }} />}
                  sx={{ bgcolor: toneAlpha('blue', 0.1), color: accent, fontWeight: 800, fontSize: '0.68rem', height: 22 }}
                />
              )}

              <Box sx={{ flexGrow: 1 }} />

              <TextField
                select size="small" label="Trier par" value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{
                  bgcolor: 'background.paper',
                  '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                  minWidth: 210
                }}
              >
                {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Box>
          </Card>

          {/* ── Résultats ─────────────────────────────────────────── */}
          {loading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 12 }}>
              <CircularProgress sx={{ color: accent }} />
            </Box>
          )}

          {!loading && groups.length === 0 && (
            <Card sx={{ py: 10, textAlign: 'center', borderRadius: '20px', border: `1px solid ${grey(100)}`, boxShadow: 'none' }}>
              <DescriptionIcon sx={{ fontSize: 44, color: grey(300), mb: 1.5 }} />
              <Typography sx={{ fontWeight: 800, color: 'text.secondary', mb: 0.5 }}>
                Aucun compte rendu disponible
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', color: 'text.disabled' }}>
                {search
                  ? 'Aucune session ne correspond à cette recherche.'
                  : 'Une session apparaît ici dès qu’un questionnaire y a été rempli.'}
              </Typography>
            </Card>
          )}

          {!loading && groups.length > 0 && !openFormationId && (
            <Grid container spacing={2}>
              {formationCards.map((formation) => (
                <Grid item xs={12} sm={6} lg={4} xl={3} key={formation.formation_id}>
                  <FormationResponseCard
                    formation={formation}
                    accent={accent}
                    showScore={false}
                    onOpen={(f) => openFormation(f.formation_id)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {!loading && groups.length > 0 && openFormationId && (
            <>
              <Card
                sx={{
                  p: 2,
                  mb: 2,
                  borderRadius: '18px',
                  border: `1px solid ${grey(100)}`,
                  boxShadow: (theme) => `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap'
                }}
              >
                <Button
                  onClick={backToFormations}
                  startIcon={<ArrowLeftIcon style={{ fontSize: 13 }} />}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, color: 'text.secondary' }}
                >
                  Toutes les formations
                </Button>
                <Divider orientation="vertical" flexItem sx={{ borderColor: 'divider' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentFormation?.formation_name || 'Formation inconnue'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontWeight: 600 }}>
                    {formationSessions.length} session{formationSessions.length > 1 ? 's' : ''}
                    {currentFormation?.program_number ? ` · N° ${currentFormation.program_number}` : ''}
                    {' · cliquez une session pour télécharger son compte rendu'}
                  </Typography>
                </Box>
              </Card>

              <Grid container spacing={2}>
                {formationSessions.map((group) => {
                  const targeted = contextSessionId !== null
                    && String(group.session_id) === String(contextSessionId);
                  return (
                    <Grid item xs={12} sm={6} lg={4} xl={3} key={groupKey(group)}>
                      <Box
                        sx={targeted
                          ? { borderRadius: '22px', outline: `2px solid ${accent}`, outlineOffset: '2px', height: '100%' }
                          : { height: '100%' }}
                      >
                        <SessionResponseCard
                          group={group}
                          accent={accent}
                          showScore={false}
                          cta={downloadCta}
                          busy={String(downloadingId) === String(group.session_id)}
                          onOpen={handleDownload}
                        />
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </Container>

        <ScrollJumpButtons
          getTarget={getPageScroller}
          watch={`${openFormationId ?? 'all'}-${sortedGroups.length}-${loading}`}
          accent={accent}
          offset={24}
          fixed
        />
      </Box>
    </>
  );
};

export default ComptesRendus;
